import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, FileText, Handshake, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  BoutonMint,
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { Modale } from '../components/ui/Modale';
import { ParcoursCommercial } from '../components/commercial/ParcoursCommercial';
import { usePerimetre } from '../site/usePerimetre';
import { TOUS_LES_SITES } from '../site/context';
import { useSites } from '../lib/useSites';
import { useTaillePage } from '../lib/useTaillePage';
import {
  LIBELLES_FILTRE_BESOIN,
  useCreerEntreprise,
  useEnregistrerBesoin,
  useEntreprises,
  useModifierBesoin,
  useModifierEntreprise,
  useSupprimerBesoin,
  type Besoin,
  type FiltreBesoin,
  type LigneEntreprise,
  urlFicheBesoin,
  type ModificationBesoin,
  type StatutRelation,
  type TypeContratBesoin,
} from '../lib/useEntreprises';
import { ChampsFicheBesoin } from '../components/commercial/ChampsFicheBesoin';
import { DetailBesoin } from '../components/commercial/DetailBesoin';
import { ModaleRecueilBesoin } from '../components/commercial/ModaleRecueilBesoin';
import { ModaleOrganisation } from '../components/repertoire/ModaleOrganisation';
import type { FicheOrganisation } from '../lib/usePedagogique';
import {
  CLASSE_CHAMP,
  emailValide,
  nombrePostes,
  postesValides,
  tachesRemplies,
  valeursInitiales,
  type ValeursFiche,
} from '../lib/fiche-besoin';
import { ErreurApi } from '../lib/api';
import { useToast } from '../components/ui/useToast';
import { useEcranEtroit } from '../lib/useEcranEtroit';
import { usePiegeFocus } from '../lib/usePiegeFocus';

function jour(valeur: string | null): string {
  if (!valeur) return 'jamais';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/** Ce que la fenêtre d'un besoin montre : sa fiche, sa modification, sa suppression. */
type ModeBesoin = 'detail' | 'edition' | 'suppression';

/**
 * Vue de travail du répertoire : « qui je traite maintenant ».
 *
 * `toutes` le répertoire entier ; `besoins` les entreprises qui ont un poste à
 * pourvoir, filtré par l'API ; `relances` celles dont l'échange est en retard,
 * calculé ligne par ligne par l'API et trié ici.
 */
type VueTravail = 'toutes' | 'besoins' | 'relances';

/**
 * Écran « Entreprises & prospection » du pôle commercial (CDC §5.1).
 *
 * Répertoire commun aux pôles : la même entreprise sert à la prospection, au
 * matching et au contrat. Les postes à pourvoir viennent de la fiche de recueil
 * des besoins, seule source des chiffres affichés.
 *
 * L'écran tient en deux temps : la liste répond à « qui je traite », le panneau
 * de droite à « qu'est-ce que je fais de cette entreprise ». Aucune entreprise
 * n'est sélectionnée d'office — un formulaire de recueil pré-rempli sur une
 * entreprise que la personne n'a pas choisie créerait le besoin sur la mauvaise.
 */
export function ComEntreprisesPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const { site } = usePerimetre();

  const [vue, setVue] = useState<VueTravail>('toutes');
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState<StatutRelation | ''>('');
  const [besoinChoisi, setBesoinChoisi] = useState<FiltreBesoin | ''>('');
  const [creation, setCreation] = useState(false);

  // La vue commande le filtre, jamais l'inverse : l'onglet « Postes à pourvoir »
  // interroge l'API, l'onglet « Relance attendue » trie les lignes déjà reçues.
  // Le filtre fin n'existe que dans la vue « Toutes », sinon deux commandes
  // diraient la même chose en se contredisant à l'écran.
  const besoin: FiltreBesoin | '' =
    vue === 'besoins' ? 'OUVERT' : vue === 'toutes' ? besoinChoisi : '';
  const relancesSeules = vue === 'relances';
  const [selectionId, setSelectionId] = useState<string | null>(null);
  const [besoinOuvert, setBesoinOuvert] = useState<{ id: string; mode: ModeBesoin } | null>(
    null,
  );
  const [recueilOuvert, setRecueilOuvert] = useState(false);
  const [editionOuverte, setEditionOuverte] = useState(false);

  const filtres = {
    site,
    ...(statut ? { statut } : {}),
    ...(recherche.trim() ? { recherche: recherche.trim() } : {}),
    ...(besoin ? { besoin } : {}),
  };

  const { data, isLoading, error } = useEntreprises(filtres);
  const { data: tousLesSites } = useSites();
  const creer = useCreerEntreprise();
  const modifier = useModifierEntreprise();
  const enregistrerBesoin = useEnregistrerBesoin();
  const modifierBesoin = useModifierBesoin();
  const supprimerBesoin = useSupprimerBesoin();

  const compteurs = data?.compteurs;

  // La relance attendue est calculée par l'API ligne par ligne, mais n'est pas
  // un filtre de la requête : elle se lit sur les lignes déjà reçues.
  const entreprises = useMemo(() => {
    const lignes = data?.entreprises ?? [];
    return relancesSeules ? lignes.filter((ligne) => ligne.relanceAttendue) : lignes;
  }, [data?.entreprises, relancesSeules]);

  // La sélection est tenue par identifiant, jamais par objet : après une
  // mutation, le panneau doit montrer la ligne rafraîchie, pas la copie figée
  // au moment du clic.
  const selection = selectionId
    ? entreprises.find((entree) => entree.id === selectionId) ?? null
    : null;
  // Sous `lg`, le panneau est une feuille par-dessus la liste : une fenêtre
  // au clavier (TF-341). Au-delà, il est posé à côté et ne retient rien.
  const feuille = useEcranEtroit(1024) && selection !== null;
  const panneauSelection = useRef<HTMLDivElement>(null);
  usePiegeFocus(panneauSelection, feuille, () => setSelectionId(null), { focusInitial: 'premier' });

  // Même principe pour le besoin ouvert : il est relu dans l'entreprise à chaque
  // rendu, donc la fenêtre montre l'état d'après enregistrement, et se referme
  // d'elle-même si le besoin a disparu.
  const besoinAffiche = besoinOuvert
    ? selection?.besoins.find((entree) => entree.id === besoinOuvert.id) ?? null
    : null;

  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  const filtresActifs = Boolean(statut || besoinChoisi || recherche.trim() || vue !== 'toutes');

  const effacerFiltres = (): void => {
    setVue('toutes');
    setStatut('');
    setBesoinChoisi('');
    setRecherche('');
  };

  useEffect(() => {
    setPage(1);
  }, [site, statut, besoin, recherche, relancesSeules, taillePage]);

  useEffect(() => {
    if (besoinOuvert && !besoinAffiche) setBesoinOuvert(null);
  }, [besoinOuvert, besoinAffiche]);

  // La fiche de recueil porte sur l'entreprise sélectionnée : elle se ferme avec
  // elle, sinon un besoin serait recueilli sur une entreprise que la personne ne
  // voit plus à l'écran.
  useEffect(() => {
    if (!selection) {
      setRecueilOuvert(false);
      setEditionOuverte(false);
    }
  }, [selection]);

  useEffect(() => {
    if (!selection) return;
    const auClavier = (evenement: KeyboardEvent): void => {
      if (evenement.key === 'Escape') setSelectionId(null);
    };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [selection]);

  const total = entreprises.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const entreprisesAffichees = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return entreprises.slice(debut, debut + taillePage);
  }, [entreprises, page, taillePage]);

  const vues: { cle: VueTravail; libelle: string; compte?: number }[] = [
    { cle: 'toutes', libelle: 'Toutes' },
    { cle: 'besoins', libelle: 'Postes à pourvoir', compte: compteurs?.postesOuverts },
    { cle: 'relances', libelle: 'Relance attendue', compte: compteurs?.relancesAttendues },
  ];

  return (
    <div className="mx-auto max-w-[1500px] space-y-5 p-4 sm:p-6">
      <EnteteEcran
        titre="Entreprises & prospection"
        sousTitre="Chargé(e) de relation entreprise · fiche de recueil des besoins"
        actions={
          <BoutonPrincipal
            onClick={() => {
              creer.reset();
              setCreation(true);
            }}
          >
            + Nouvelle entreprise
          </BoutonPrincipal>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <BarreSites />
        <ParcoursCommercial etape="besoin" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Entreprises partenaires"
          valeur={compteurs?.partenaires ?? '—'}
          detail="sur ce périmètre"
          ton="navy"
        />
        <Compteur
          libelle="Postes ouverts"
          valeur={compteurs?.postesOuverts ?? '—'}
          detail="à pourvoir"
          ton="mint"
        />
        <Compteur
          libelle="En négociation"
          valeur={compteurs?.enNegociation ?? '—'}
          detail={
            compteurs && compteurs.relancesAttendues > 0
              ? `${compteurs.relancesAttendues} relance(s) attendue(s)`
              : 'relance à J+7'
          }
          ton="warm"
        />
        <Compteur
          libelle="À prospecter"
          valeur={compteurs?.aProspecter ?? '—'}
          detail="premier contact à établir"
          ton="blue"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-white p-3 shadow-card">
        <div className="flex flex-wrap items-center gap-1.5">
          {vues.map((entree) => (
            <button
              key={entree.cle}
              type="button"
              onClick={() => setVue(entree.cle)}
              aria-pressed={vue === entree.cle}
              className={`min-h-[44px] rounded-lg px-3 py-2 text-xs font-bold transition ${
                vue === entree.cle
                  ? 'bg-codialis-navy text-white'
                  : 'bg-gray-50 text-codialis-gray hover:bg-gray-100'
              }`}
            >
              {entree.libelle}
              {entree.compte !== undefined && entree.compte > 0 && (
                <span className={vue === entree.cle ? ' text-white/70' : ' text-codialis-gray/70'}>
                  {' '}
                  · {entree.compte}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="relative">
            <span className="sr-only">Rechercher une entreprise</span>
            <input
              type="search"
              value={recherche}
              onChange={(evenement) => setRecherche(evenement.target.value)}
              placeholder="Nom, secteur, SIRET"
              className="min-h-[44px] w-full min-w-[12rem] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-codialis-mint sm:w-56"
            />
          </label>
          <label>
            <span className="sr-only">Filtrer par statut de relation</span>
            <select
              value={statut}
              onChange={(evenement) => setStatut(evenement.target.value as StatutRelation | '')}
              className="min-h-[44px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs"
            >
              <option value="">Tous les statuts</option>
              {(data?.statuts ?? []).map((entree) => (
                <option key={entree.valeur} value={entree.valeur}>
                  {entree.libelle}
                </option>
              ))}
            </select>
          </label>
          {/* Le filtre fin ne s'affiche que dans la vue « Toutes » : dans les
              autres, la vue porte déjà le filtre besoin. */}
          {vue === 'toutes' && (
            <label>
              <span className="sr-only">Filtrer par besoin recueilli</span>
              <select
                value={besoinChoisi}
                onChange={(evenement) =>
                  setBesoinChoisi(evenement.target.value as FiltreBesoin | '')
                }
                className="min-h-[44px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs"
              >
                <option value="">Tous les besoins</option>
                {LIBELLES_FILTRE_BESOIN.map((entree) => (
                  <option key={entree.valeur} value={entree.valeur}>
                    {entree.libelle}
                  </option>
                ))}
              </select>
            </label>
          )}
          {filtresActifs && (
            <button
              type="button"
              onClick={effacerFiltres}
              className="min-h-[44px] rounded-lg px-3 py-2 text-xs font-semibold text-codialis-sky hover:underline"
            >
              Effacer les filtres
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0">
          <Carte titre="Répertoire entreprises" sansBordure>
            {error && (
              <div className="p-4 sm:p-6">
                <MessageErreur
                  message={
                    error instanceof ErreurApi
                      ? error.message
                      : 'Le répertoire n’a pas pu être chargé.'
                  }
                />
              </div>
            )}
            {isLoading && <Chargement libelle="Chargement du répertoire…" />}

            {!isLoading && !error && (
              <div className="relative overflow-x-auto">
                <table className="w-full min-w-[900px] text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      {[
                        'Entreprise',
                        'Secteur',
                        'Site',
                        'Besoins',
                        'Statut',
                        'Dernier échange',
                        '',
                      ].map((colonne, index) => (
                        <th
                          key={colonne || index}
                          className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                        >
                          {colonne || <span className="sr-only">Actions</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {total === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 sm:px-6 py-12 text-center text-sm text-codialis-gray">
                          {vue === 'relances'
                            ? 'Aucune relance en attente sur ce périmètre.'
                            : 'Aucune entreprise ne correspond aux filtres actifs.'}
                        </td>
                      </tr>
                    )}
                    {entreprisesAffichees.map((entreprise) => (
                      <LigneRepertoire
                        key={entreprise.id}
                        entreprise={entreprise}
                        active={selectionId === entreprise.id}
                        onSelect={() => setSelectionId(entreprise.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!isLoading && !error && total > 0 && (
              <Pagination
                page={page}
                pages={pages}
                total={total}
                affiches={entreprisesAffichees.length}
                taillePage={taillePage}
                onPage={setPage}
                onChangerTaille={setTaillePage}
                uniteLabel="entreprise"
              />
            )}
          </Carte>
        </div>

        {/* Sous `lg`, le panneau devient une feuille ancrée en bas d'écran : sur
            tablette, il serait sinon rejeté sous un tableau d'une pleine page. */}
        {selection && (
          <div
            className="fixed inset-0 z-40 bg-codialis-navy/40 lg:hidden"
            onClick={() => setSelectionId(null)}
            aria-hidden="true"
          />
        )}
        <div
          ref={panneauSelection}
          {...(feuille && selection ? { role: 'dialog', 'aria-modal': true, 'aria-label': `Entreprise ${selection.denomination}` } : {})}
          className={
            selection
              ? 'fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto lg:sticky lg:top-6 lg:z-auto lg:max-h-[calc(100vh-3rem)]'
              : 'hidden lg:sticky lg:top-6 lg:block'
          }
        >
          <PanneauEntreprise
            entreprise={selection}
            statuts={data?.statuts ?? []}
            enCoursEntreprise={modifier.isPending}
            enCoursBesoin={modifierBesoin.isPending}
            onFermer={() => setSelectionId(null)}
            onChangerStatut={(valeur) =>
              selection && modifier.mutate({ id: selection.id, statutRelation: valeur })
            }
            onContactPris={() =>
              selection &&
              modifier.mutate(
                { id: selection.id, contactPris: true },
                { onSuccess: () => toast('Échange noté') },
              )
            }
            onCloreBesoin={(besoinId, valeur) =>
              modifierBesoin.mutate(
                { besoinId, statut: valeur },
                {
                  onSuccess: () =>
                    toast(valeur === 'POURVU' ? 'Besoin marqué pourvu' : 'Besoin abandonné'),
                },
              )
            }
            onOuvrirBesoin={(besoinId, mode) => setBesoinOuvert({ id: besoinId, mode })}
            onLancerMatching={() => navigate('/com-matching')}
            onOuvrirRecueil={() => {
              enregistrerBesoin.reset();
              setRecueilOuvert(true);
            }}
            onOuvrirEdition={() => {
              modifier.reset();
              setEditionOuverte(true);
            }}
          />
        </div>
      </div>

      <FenetreBesoin
        besoin={besoinAffiche}
        mode={besoinOuvert?.mode ?? 'detail'}
        formations={data?.formations ?? []}
        typesContrat={data?.typesContrat ?? []}
        enCoursModification={modifierBesoin.isPending}
        enCoursSuppression={supprimerBesoin.isPending}
        erreur={
          modifierBesoin.error instanceof ErreurApi
            ? modifierBesoin.error.message
            : supprimerBesoin.error instanceof ErreurApi
              ? supprimerBesoin.error.message
              : null
        }
        onFermer={() => {
          // L'erreur d'une mutation survit a son echec : elle est effacee avec
          // la fenetre, sinon un refus de suppression resterait affiche au
          // dessus du formulaire de modification ouvert ensuite.
          modifierBesoin.reset();
          supprimerBesoin.reset();
          setBesoinOuvert(null);
        }}
        onMode={(mode) => {
          modifierBesoin.reset();
          supprimerBesoin.reset();
          setBesoinOuvert((ouvert) => (ouvert ? { ...ouvert, mode } : ouvert));
        }}
        onModifier={(modification) => {
          if (!besoinAffiche) return;
          modifierBesoin.mutate(
            { besoinId: besoinAffiche.id, ...modification },
            {
              onSuccess: () => {
                toast('Besoin mis à jour');
                setBesoinOuvert((ouvert) => (ouvert ? { ...ouvert, mode: 'detail' } : ouvert));
              },
            },
          );
        }}
        onSupprimer={() => {
          if (!besoinAffiche) return;
          supprimerBesoin.mutate(besoinAffiche.id, {
            onSuccess: () => {
              toast('Besoin supprimé');
              setBesoinOuvert(null);
            },
          });
        }}
      />

      {/* Création et correction passent par la même fenêtre que le répertoire
          commun : une seule fiche entreprise dans toute l'application. */}
      {creation && (
        <ModaleOrganisation
          ouverte
          natureImposee="ENTREPRISE"
          statutsRelation={data?.statuts ?? []}
          typesPartenaire={[]}
          secteurs={data?.secteursSuggeres ?? []}
          typesEmployeur={data?.typesEmployeurSuggeres ?? []}
          employeursSpecifiques={data?.employeursSpecifiquesSuggeres ?? []}
          sitesDisponibles={(tousLesSites?.sites ?? []).map((entree) => entree.code)}
          sitesParDefaut={site === TOUS_LES_SITES ? [] : [site]}
          enCours={creer.isPending}
          erreur={creer.error instanceof ErreurApi ? creer.error.message : null}
          onFermer={() => {
            creer.reset();
            setCreation(false);
          }}
          onEnregistrer={(sortie) => {
            if (!sortie.entreprise) return;
            // `effectif` non saisi arrive à `null` — utile à la correction pour
            // effacer, refusé à la création : on l'omet.
            const { effectif, ...entree } = sortie.entreprise;
            creer.mutate(
              { ...entree, ...(effectif === null ? {} : { effectif }) },
              {
                onSuccess: () => {
                  toast('Entreprise enregistrée');
                  setCreation(false);
                },
              },
            );
          }}
        />
      )}

      {editionOuverte && selection && (
        <ModaleOrganisation
          key={selection.id}
          ouverte
          fiche={ficheDeLaLigne(selection)}
          maitresExistants={selection.maitresApprentissage}
          statutsRelation={data?.statuts ?? []}
          typesPartenaire={[]}
          secteurs={data?.secteursSuggeres ?? []}
          typesEmployeur={data?.typesEmployeurSuggeres ?? []}
          employeursSpecifiques={data?.employeursSpecifiquesSuggeres ?? []}
          sitesDisponibles={(tousLesSites?.sites ?? []).map((entree) => entree.code)}
          enCours={modifier.isPending}
          erreur={modifier.error instanceof ErreurApi ? modifier.error.message : null}
          onFermer={() => {
            modifier.reset();
            setEditionOuverte(false);
          }}
          onEnregistrer={(sortie) => {
            if (!sortie.entreprise) return;
            modifier.mutate(
              { id: selection.id, ...sortie.entreprise },
              {
                onSuccess: () => {
                  toast('Fiche entreprise mise à jour');
                  setEditionOuverte(false);
                },
              },
            );
          }}
        />
      )}

      <ModaleRecueilBesoin
        employeur={
          recueilOuvert && selection
            ? {
                id: selection.id,
                denomination: selection.denomination,
                siret: selection.siret,
                telephone: selection.telephone,
                email: selection.email,
              }
            : null
        }
        formations={data?.formations ?? []}
        typesContrat={data?.typesContrat ?? []}
        enCours={enregistrerBesoin.isPending}
        erreur={
          enregistrerBesoin.error instanceof ErreurApi ? enregistrerBesoin.error.message : null
        }
        onFermer={() => {
          enregistrerBesoin.reset();
          setRecueilOuvert(false);
        }}
        onRecueillir={(entree, lancerMatching) => {
          if (!selection) return;
          enregistrerBesoin.mutate(
            { entrepriseId: selection.id, ...entree },
            {
              onSuccess: (resultat) => {
                setRecueilOuvert(false);
                toast(
                  `Besoin transmis au pôle commercial${
                    resultat.informes > 0 ? ` · ${resultat.informes} personne(s) prévenue(s)` : ''
                  }`,
                );
                // Le besoin part au matching dans tous les cas : seule la
                // navigation est en option, pour enchaîner un second recueil
                // ou éditer la fiche sans quitter le répertoire.
                if (lancerMatching) navigate('/com-matching');
              },
            },
          );
        }}
      />
    </div>
  );
}

function LigneRepertoire({
  entreprise,
  active,
  onSelect,
}: {
  entreprise: LigneEntreprise;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <tr
      onClick={onSelect}
      onKeyDown={(evenement) => {
        if (evenement.key === 'Enter' || evenement.key === ' ') {
          evenement.preventDefault();
          onSelect();
        }
      }}
      tabIndex={0}
      aria-current={active || undefined}
      className={`cursor-pointer border-l-2 transition focus:outline-none focus:ring-2 focus:ring-inset focus:ring-codialis-mint ${
        active
          ? 'border-codialis-mint bg-codialis-mint/10'
          : 'border-transparent hover:bg-gray-50'
      }`}
    >
      <td className="px-4 py-3">
        {/* Le nom mène au dossier, partout dans l'application : c'est le geste
            qu'on fait sans y penser. La ligne, elle, ouvre le panneau de
            prospection — d'où le `stopPropagation`. */}
        <Link
          to={`/ped-repertoire/${entreprise.id}`}
          onClick={(evenement) => evenement.stopPropagation()}
          className="font-semibold text-codialis-navy hover:text-codialis-sky hover:underline"
        >
          {entreprise.denomination}
        </Link>
        <div className="font-mono text-[10px] text-codialis-gray">{entreprise.siret}</div>
      </td>
      <td className="px-4 py-3 text-xs text-codialis-gray">{entreprise.secteur ?? '—'}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {entreprise.sites.map((code) => (
            <PastilleSite key={code} site={code} />
          ))}
        </div>
      </td>
      <td className="px-4 py-3">
        {entreprise.postesOuverts > 0 ? (
          <Pastille
            texte={`${entreprise.postesOuverts} poste${entreprise.postesOuverts > 1 ? 's' : ''}`}
            ton="mint"
          />
        ) : (
          <span className="text-xs text-codialis-gray">—</span>
        )}
        {entreprise.apprenantsAccueillis > 0 && (
          <div className="mt-1 text-[10px] text-codialis-gray">
            {entreprise.apprenantsAccueillis} apprenant
            {entreprise.apprenantsAccueillis > 1 ? 's' : ''} accueilli
            {entreprise.apprenantsAccueillis > 1 ? 's' : ''}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <Pastille texte={entreprise.statutLibelle} ton={entreprise.statutTon} />
      </td>
      <td className="px-4 py-3 text-xs text-codialis-gray">
        {jour(entreprise.dernierContactLe)}
        {entreprise.relanceAttendue && (
          <div className="mt-1">
            <Pastille texte="Relance attendue" ton="warning" />
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <Link
          to={`/ped-repertoire/${entreprise.id}`}
          onClick={(evenement) => evenement.stopPropagation()}
          className="whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline"
        >
          Ouvrir le dossier
        </Link>
      </td>
    </tr>
  );
}

function PanneauEntreprise({
  entreprise,
  statuts,
  enCoursEntreprise,
  enCoursBesoin,
  onFermer,
  onChangerStatut,
  onContactPris,
  onCloreBesoin,
  onOuvrirBesoin,
  onLancerMatching,
  onOuvrirRecueil,
  onOuvrirEdition,
}: {
  entreprise: LigneEntreprise | null;
  statuts: { valeur: StatutRelation; libelle: string }[];
  enCoursEntreprise: boolean;
  enCoursBesoin: boolean;
  onFermer: () => void;
  onChangerStatut: (statut: StatutRelation) => void;
  onContactPris: () => void;
  onCloreBesoin: (besoinId: string, statut: 'POURVU' | 'ABANDONNE') => void;
  onOuvrirBesoin: (besoinId: string, mode: ModeBesoin) => void;
  onLancerMatching: () => void;
  onOuvrirRecueil: () => void;
  onOuvrirEdition: () => void;
}): JSX.Element {
  if (!entreprise) {
    return (
      <Carte titre="Fiche de recueil des besoins" sansBordure>
        <div className="flex flex-col items-center gap-3 px-4 sm:px-6 py-12 text-center">
          <svg
            className="h-8 w-8 text-gray-300"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1m-1 4h1m-1 4h1m4-8h1m-1 4h1m-1 4h1"
            />
          </svg>
          <p className="text-sm text-codialis-gray">
            Sélectionnez une entreprise dans le répertoire pour consulter ses besoins, noter un
            échange et recueillir un nouveau besoin.
          </p>
        </div>
      </Carte>
    );
  }

  const ouverts = entreprise.besoins.filter((besoin) => besoin.statut === 'OUVERT');
  const clos = entreprise.besoins.filter((besoin) => besoin.statut !== 'OUVERT');

  return (
    <div className="overflow-hidden rounded-t-2xl bg-white shadow-card lg:rounded-2xl">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 sm:px-6 py-4">
        <div className="min-w-0">
          <h3 className="font-display truncate font-bold text-codialis-navy">
            {entreprise.denomination}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-codialis-gray">
            <span className="font-mono">{entreprise.siret}</span>
            {entreprise.secteur && <span>· {entreprise.secteur}</span>}
            {entreprise.sites.map((code) => (
              <PastilleSite key={code} site={code} />
            ))}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onOuvrirEdition}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
          Modifier la fiche
        </button>
        <button
          type="button"
          onClick={onFermer}
          aria-label="Fermer le panneau"
          className="rounded-lg p-1 text-codialis-gray transition hover:bg-gray-50 hover:text-codialis-navy"
        >
          <svg aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        </div>
      </div>

      <div className="space-y-2 border-b border-gray-100 px-4 sm:px-6 py-4 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-bold uppercase tracking-wide text-codialis-gray">
            Relation
          </span>
          {entreprise.relanceAttendue && <Pastille texte="Relance attendue" ton="warning" />}
        </div>
        <select
          aria-label="Relation"
          value={entreprise.statutRelation}
          onChange={(evenement) => onChangerStatut(evenement.target.value as StatutRelation)}
          disabled={enCoursEntreprise}
          className={CLASSE_CHAMP}
        >
          {statuts.map((entree) => (
            <option key={entree.valeur} value={entree.valeur}>
              {entree.libelle}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gray-50 px-3 py-2">
          <div className="flex min-w-0 items-center gap-1.5 text-xs text-codialis-gray">
            <svg
              className="h-4 w-4 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {TRACES_ICONE.horloge}
            </svg>
            <span className="truncate">
              Dernier échange{' '}
              <span className="font-semibold text-codialis-navy">
                {jour(entreprise.dernierContactLe)}
              </span>
            </span>
          </div>
          <button
            type="button"
            onClick={onContactPris}
            disabled={enCoursEntreprise}
            className="inline-flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg border border-sky-200 bg-white px-2.5 text-xs font-semibold text-codialis-sky transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-[44px]"
          >
            <svg
              className="h-4 w-4 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {TRACES_ICONE.messagePlus}
            </svg>
            Noter un échange
          </button>
        </div>
        {(entreprise.contact || entreprise.telephone || entreprise.email) && (
          <div className="space-y-0.5 pt-1 text-xs text-codialis-gray">
            {entreprise.contact && <div className="text-codialis-navy">{entreprise.contact}</div>}
            {entreprise.telephone && <div>{entreprise.telephone}</div>}
            {entreprise.email && <div className="truncate">{entreprise.email}</div>}
          </div>
        )}
        <DetailsEntreprise entreprise={entreprise} />
      </div>

      <div className="space-y-3 border-b border-gray-100 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-bold uppercase tracking-wide text-codialis-gray">
            Besoins
          </span>
          <span className="text-xs text-codialis-gray">
            {ouverts.length} ouvert{ouverts.length > 1 ? 's' : ''} · {clos.length} clos
          </span>
        </div>
        {entreprise.besoins.length === 0 && (
          <p className="text-sm text-codialis-gray">
            Aucun besoin recueilli pour cette entreprise.
          </p>
        )}
        {entreprise.besoins.map((besoin) => (
          <CarteBesoin
            key={besoin.id}
            besoin={besoin}
            enCours={enCoursBesoin}
            onClore={(valeur) => onCloreBesoin(besoin.id, valeur)}
            onOuvrir={(mode) => onOuvrirBesoin(besoin.id, mode)}
            onLancerMatching={onLancerMatching}
          />
        ))}
      </div>

      {/* La fiche de recueil s'ouvre en fenêtre : dépliée ici, elle poussait les
          besoins déjà recueillis hors de l'écran au moment précis où on les
          relit pour en saisir un nouveau. */}
      <div className="px-4 sm:px-6 py-4">
        <BoutonSecondaire onClick={onOuvrirRecueil}>+ Recueillir un besoin</BoutonSecondaire>
      </div>
    </div>
  );
}

/**
 * Le panneau de prospection est aussi la fiche entreprise rapide : les données
 * déjà collectées ne doivent pas obliger à ouvrir le répertoire pour être
 * vérifiées. Les valeurs absentes sont volontairement masquées pour garder la
 * lecture compacte.
 */
function DetailsEntreprise({ entreprise }: { entreprise: LigneEntreprise }): JSX.Element | null {
  const adresse = [
    [entreprise.numeroVoie, entreprise.voie].filter(Boolean).join(' '),
    entreprise.complementAdresse,
    [entreprise.codePostal, entreprise.ville].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ') || entreprise.adresse;
  const employeur = entreprise.secteurEmployeur
    ? entreprise.secteurEmployeur === 'PUBLIC' ? 'Public' : 'Privé'
    : null;
  const donnees: Array<[string, string | number | null]> = [
    ['Adresse', adresse],
    ['Code APE', entreprise.codeNaf],
    ['Employeur', employeur],
    ['Employeur spécifique', entreprise.employeurSpecifique],
    ['Effectif salariés', entreprise.effectif],
    ['Caisse de retraite complémentaire', entreprise.caisseRetraite],
    ['Gérant', entreprise.gerant],
    ['OPCO', entreprise.opco],
    ['Convention collective', entreprise.idcc],
    ['Site internet', entreprise.siteWeb],
  ];
  const renseignees = donnees.filter(([, valeur]) => valeur !== null && valeur !== '');
  if (renseignees.length === 0) return null;

  return (
    <dl className="grid grid-cols-1 gap-x-3 gap-y-1.5 border-t border-gray-100 pt-3 text-xs sm:grid-cols-2">
      {renseignees.map(([libelle, valeur]) => (
        <div key={libelle} className={libelle === 'Adresse' ? 'sm:col-span-2' : ''}>
          <dt className="font-semibold text-codialis-gray">{libelle}</dt>
          <dd className="break-words text-codialis-navy">{valeur}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Fenêtre d'un besoin : consultation, modification, suppression.
 *
 * Les trois modes partagent la même fenêtre parce qu'ils portent sur la même
 * fiche : on ouvre un besoin, puis on décide ce qu'on en fait. Le formulaire est
 * remonté par sa `key` à chaque besoin, sinon les valeurs du précédent
 * resteraient affichées sur le suivant.
 */
function FenetreBesoin({
  besoin,
  mode,
  formations,
  typesContrat,
  enCoursModification,
  enCoursSuppression,
  erreur,
  onFermer,
  onMode,
  onModifier,
  onSupprimer,
}: {
  besoin: Besoin | null;
  mode: ModeBesoin;
  formations: { code: string; titre: string }[];
  typesContrat: { valeur: TypeContratBesoin; libelle: string }[];
  enCoursModification: boolean;
  enCoursSuppression: boolean;
  erreur: string | null;
  onFermer: () => void;
  onMode: (mode: ModeBesoin) => void;
  onModifier: (modification: ModificationBesoin) => void;
  onSupprimer: () => void;
}): JSX.Element | null {
  if (!besoin) return null;

  return (
    <Modale
      ouverte
      onFermer={onFermer}
      titre={`Besoin · ${besoin.nombrePostes} poste${besoin.nombrePostes > 1 ? 's' : ''}`}
      sousTitre={besoin.formation ?? 'Formation à définir'}
    >
      <div className="space-y-4 p-4 sm:p-6">
        {erreur && <MessageErreur message={erreur} />}

        {mode === 'detail' && (
          <DetailBesoin besoin={besoin} onEditer={() => onMode('edition')} />
        )}

        {mode === 'edition' && (
          <FormulaireBesoin
            key={besoin.id}
            besoin={besoin}
            formations={formations}
            typesContrat={typesContrat}
            enCours={enCoursModification}
            onAnnuler={() => onMode('detail')}
            onEnregistrer={onModifier}
          />
        )}

        {mode === 'suppression' && (
          <div className="space-y-4">
            <p className="text-sm text-codialis-navy">
              Supprimer définitivement ce besoin de {besoin.nombrePostes} poste
              {besoin.nombrePostes > 1 ? 's' : ''}
              {besoin.formation ? ` en ${besoin.formation}` : ''} ? Les candidats déjà
              rapprochés perdent ce rapprochement.
            </p>
            <p className="text-xs text-codialis-gray">
              Un besoin qui n'a pas abouti se clôt par « Abandonner » : il reste alors dans
              l'historique de l'entreprise. La suppression, elle, ne laisse rien.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <BoutonSecondaire onClick={() => onMode('detail')} disabled={enCoursSuppression}>
                Annuler
              </BoutonSecondaire>
              <button
                type="button"
                onClick={onSupprimer}
                disabled={enCoursSuppression}
                className="min-h-[44px] rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {enCoursSuppression ? 'Suppression…' : 'Supprimer le besoin'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modale>
  );
}

/**
 * Modification d'un besoin : la fiche de recueil entière, rouverte.
 *
 * Les champs facultatifs laissés vides sont envoyés à `null`, ce que l'API sait
 * effacer : sans cela, une date saisie par erreur ne pourrait plus être
 * retirée, seulement remplacée.
 */
function FormulaireBesoin({
  besoin,
  formations,
  typesContrat,
  enCours,
  onAnnuler,
  onEnregistrer,
}: {
  besoin: Besoin;
  formations: { code: string; titre: string }[];
  typesContrat: { valeur: TypeContratBesoin; libelle: string }[];
  enCours: boolean;
  onAnnuler: () => void;
  onEnregistrer: (modification: ModificationBesoin) => void;
}): JSX.Element {
  const [valeurs, setValeurs] = useState<ValeursFiche>(() => valeursInitiales(besoin, ''));
  const valide = postesValides(valeurs.postes) && emailValide(valeurs.candidatEmail);

  return (
    <form
      className="space-y-5"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (enCours || !valide) return;
        onEnregistrer({
          nombrePostes: nombrePostes(valeurs.postes),
          formationCode: valeurs.formationCode || null,
          dateSouhaitee: valeurs.dateSouhaitee || null,
          profilRecherche: valeurs.profilRecherche.trim() || null,
          ouvertRqth: valeurs.rqth,
          dateDemande: valeurs.dateDemande || null,
          commercial: valeurs.commercial.trim() || null,
          typeContrat: valeurs.typeContrat || null,
          tachesOffre: tachesRemplies(valeurs.taches),
          faisabilite: valeurs.faisabilite === '' ? null : valeurs.faisabilite === 'oui',
          motifFaisabilite: valeurs.motifFaisabilite.trim() || null,
          candidatNom: valeurs.candidatNom.trim() || null,
          candidatTelephone: valeurs.candidatTelephone.trim() || null,
          candidatEmail: valeurs.candidatEmail.trim() || null,
        });
      }}
    >
      <ChampsFicheBesoin
        valeurs={valeurs}
        onChange={setValeurs}
        formations={formations}
        typesContrat={typesContrat}
      />

      <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
        <BoutonSecondaire onClick={onAnnuler} disabled={enCours}>
          Annuler
        </BoutonSecondaire>
        <BoutonMint type="submit" disabled={enCours || !valide}>
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </BoutonMint>
      </div>
    </form>
  );
}

function CarteBesoin({
  besoin,
  enCours,
  onClore,
  onOuvrir,
  onLancerMatching,
}: {
  besoin: Besoin;
  enCours: boolean;
  onClore: (statut: 'POURVU' | 'ABANDONNE') => void;
  onOuvrir: (mode: ModeBesoin) => void;
  onLancerMatching: () => void;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-codialis-navy">
          {besoin.nombrePostes} poste{besoin.nombrePostes > 1 ? 's' : ''}
        </span>
        <Pastille texte={besoin.statutLibelle} ton={besoin.statutTon} />
        {besoin.ouvertRqth && <Pastille texte="Ouvert RQTH" ton="info" />}
      </div>
      <div className="mt-1 text-xs text-codialis-gray">
        {besoin.formation ?? 'Formation à définir'}
        {besoin.dateSouhaitee && ` · démarrage ${jour(besoin.dateSouhaitee)}`}
      </div>
      {besoin.profilRecherche && (
        <p className="mt-1 text-xs text-codialis-gray">{besoin.profilRecherche}</p>
      )}
      {/*
       * À gauche, les gestes qui font avancer le besoin : marquer pourvu,
       * lancer le matching, abandonner — visibles en clair, avec leur libellé.
       * À droite, un seul bouton « … » regroupe ce qui sert moins souvent
       * (consulter, éditer la fiche, modifier, supprimer) : les afficher tous
       * en icône ferait autant de cibles à lire qu'une seule action utile.
       *
       * Le défilement horizontal est confiné au groupe de gauche : le menu
       * « … » ouvre un panneau positionné en `fixed`, qu'un ancêtre en
       * `overflow` couperait net au premier scroll, quel que soit son
       * `z-index` — l'un empêche l'autre, ce n'est pas affaire d'empilement.
       */}
      <div className="mt-3 flex items-center gap-2 border-t border-gray-200/80 pt-2.5">
        <div className="relative flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto sm:gap-2">
          {besoin.statut === 'OUVERT' && (
            <>
              <ActionBesoin
                libelle="Pourvu"
                infobulle="Marquer le poste pourvu"
                icone="coche"
                disabled={enCours}
                onClick={() => onClore('POURVU')}
              />
              <ActionBesoin
                libelle="Matching"
                infobulle="Lancer le matching pour ce besoin"
                iconeLucide={Handshake}
                disabled={enCours}
                onClick={onLancerMatching}
              />
              <ActionBesoin
                libelle="Abandon"
                infobulle="Abandonner le besoin"
                icone="croix"
                disabled={enCours}
                onClick={() => onClore('ABANDONNE')}
              />
            </>
          )}
        </div>
        <div className="shrink-0">
          <MenuActionsBesoin
            items={[
              { libelle: 'Consulter le besoin', icone: Eye, onClick: () => onOuvrir('detail') },
              { libelle: 'Éditer la fiche en PDF', icone: FileText, href: urlFicheBesoin(besoin.id) },
              // Un besoin clos n'est plus modifiable côté API : l'entrée n'est
              // pas proposée plutôt que de mener à un refus.
              ...(besoin.statut === 'OUVERT'
                ? [{ libelle: 'Modifier le besoin', icone: Pencil, onClick: () => onOuvrir('edition') }]
                : []),
              {
                libelle: 'Supprimer le besoin',
                icone: Trash2,
                danger: true,
                // L'API refuse la suppression d'un besoin déjà suivi d'un
                // positionnement, et le dit.
                onClick: () => onOuvrir('suppression'),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

interface ElementMenuBesoin {
  libelle: string;
  icone: LucideIcon;
  danger?: boolean;
  onClick?: () => void;
  /** Un lien plutôt qu'un geste : la fiche PDF s'ouvre dans un nouvel onglet. */
  href?: string;
}

/** Largeur fixe du panneau : posée aussi en JS pour calculer son ancrage. */
const LARGEUR_MENU_BESOIN = 224;

interface AncrageMenu {
  top?: number;
  bottom?: number;
  left: number;
  maxHeight: number;
}

/**
 * Regroupe les gestes secondaires sur un besoin derrière un bouton « … ».
 *
 * Même repliées, ces actions restent des cibles de 44 px et gardent leur
 * libellé complet dans le menu : seule l'icône seule disparaît de la carte,
 * jamais le texte qui la nomme.
 */
function MenuActionsBesoin({ items }: { items: ElementMenuBesoin[] }): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  // Ancrage en pixels viewport, recalculé à l'ouverture : le panneau est posé
  // en `fixed`, pas en `absolute` sous le bouton. S'il n'y a pas assez de place
  // en bas, il s'ouvre vers le haut pour ne pas être masqué par le bas de page.
  const [ancrage, setAncrage] = useState<AncrageMenu | null>(null);
  const conteneurRef = useRef<HTMLDivElement>(null);
  const boutonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!ouvert) return;
    function surClicExterieur(evenement: MouseEvent): void {
      const cible = evenement.target as Node;
      if (conteneurRef.current?.contains(cible)) return;
      setOuvert(false);
    }
    function surEchap(evenement: KeyboardEvent): void {
      if (evenement.key === 'Escape') setOuvert(false);
    }
    // Le panneau est ancré en coordonnées écran à l'ouverture : la carte peut
    // être dans un panneau qui défile (`overflow-y-auto`), et un scroll
    // décale le bouton sans bouger le panneau resté en `fixed`. On referme
    // plutôt que de suivre, sauf si le défilement se produit dans le menu lui-même.
    function surDefilement(evenement: Event): void {
      if (conteneurRef.current?.contains(evenement.target as Node)) return;
      setOuvert(false);
    }
    document.addEventListener('mousedown', surClicExterieur);
    document.addEventListener('keydown', surEchap);
    document.addEventListener('scroll', surDefilement, true);
    window.addEventListener('resize', surDefilement);
    return () => {
      document.removeEventListener('mousedown', surClicExterieur);
      document.removeEventListener('keydown', surEchap);
      document.removeEventListener('scroll', surDefilement, true);
      window.removeEventListener('resize', surDefilement);
    };
  }, [ouvert]);

  const basculer = (): void => {
    if (!ouvert && boutonRef.current) {
      const rect = boutonRef.current.getBoundingClientRect();
      const MARGE_ECRAN = 8;
      // Chaque action fait 44px min + marges/padding + séparateur (~210px pour 4 actions)
      const hauteurEstimee = items.length * 48 + 24;
      const espaceBas = window.innerHeight - rect.bottom - MARGE_ECRAN;
      const espaceHaut = rect.top - MARGE_ECRAN;

      // Si l'espace disponible en bas est inférieur à la hauteur estimée du menu et qu'il y a plus de place au-dessus,
      // on ouvre le menu vers le haut (dropup) pour éviter que le bas de page ne le cache.
      const ouvrirVersLeHaut = espaceBas < hauteurEstimee && espaceHaut > espaceBas;

      const left = Math.min(
        Math.max(MARGE_ECRAN, rect.right - LARGEUR_MENU_BESOIN),
        Math.max(MARGE_ECRAN, window.innerWidth - LARGEUR_MENU_BESOIN - MARGE_ECRAN),
      );

      if (ouvrirVersLeHaut) {
        setAncrage({
          bottom: window.innerHeight - rect.top + 4,
          left,
          maxHeight: Math.max(120, espaceHaut),
        });
      } else {
        setAncrage({
          top: rect.bottom + 4,
          left,
          maxHeight: Math.max(120, espaceBas),
        });
      }
    }
    setOuvert((precedent) => !precedent);
  };

  return (
    <div ref={conteneurRef}>
      <button
        ref={boutonRef}
        type="button"
        onClick={basculer}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-label="Autres actions sur le besoin"
        title="Autres actions"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-codialis-gray transition hover:bg-gray-50 hover:text-codialis-navy sm:h-11 sm:w-11"
      >
        <MoreHorizontal className="h-[18px] w-[18px]" aria-hidden="true" />
      </button>

      {ouvert && ancrage && (
        <div
          role="menu"
          // `fixed`, jamais `absolute` : la carte vit dans un panneau qui
          // défile (`overflow-y-auto`), qui couperait net un panneau ancré
          // dans son propre flux — quel que soit son `z-index`, un ancêtre en
          // `overflow` rogne ses enfants en position absolue avant même de
          // les empiler avec le reste de la page.
          // Le positionnement s'adapte intelligemment (vers le haut si le bas
          // est trop proche de la bordure d'écran) avec un maxHeight contrôlé.
          style={{
            top: ancrage.top,
            bottom: ancrage.bottom,
            left: ancrage.left,
            width: LARGEUR_MENU_BESOIN,
            maxHeight: ancrage.maxHeight,
          }}
          className="fixed z-[60] flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white p-1.5 text-sm shadow-xl"
        >
          <div className="overflow-y-auto">
          {items.map((item) => {
            const Icone = item.icone;
            const classe = `flex w-full min-h-[44px] items-center gap-2.5 rounded-lg px-2.5 py-2 text-left font-medium transition hover:bg-gray-50 ${
              item.danger ? 'text-red-600' : 'text-codialis-navy'
            }`;
            const contenu = (
              <>
                <Icone className="h-4 w-4 shrink-0" aria-hidden="true" />
                {item.libelle}
              </>
            );
            // La suppression, seule à ne défaire rien qu'on puisse rouvrir, est
            // séparée du reste du menu par un filet.
            const bordure = item.danger ? 'mt-1 border-t border-gray-100 pt-1.5' : '';
            return item.href ? (
              <a
                key={item.libelle}
                role="menuitem"
                href={item.href}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOuvert(false)}
                className={`${classe} ${bordure}`}
              >
                {contenu}
              </a>
            ) : (
              <button
                key={item.libelle}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOuvert(false);
                  item.onClick?.();
                }}
                className={`${classe} ${bordure}`}
              >
                {contenu}
              </button>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Traits maison pour les icônes restées sans équivalent direct dans ce qui est
 * déjà importé de Lucide ailleurs sur cet écran (`Eye`, `Pencil`, `Trash2`,
 * `FileText`, `Handshake`…) : ceux-là sont posés en ligne plutôt que d'ajouter
 * un import pour deux traits.
 */
const TRACES_ICONE = {
  coche: <path d="M20 6 9 17l-5-5" />,
  croix: (
    <>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </>
  ),
  horloge: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  messagePlus: (
    <>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M12 7v6" />
      <path d="M9 10h6" />
    </>
  ),
} as const;

/**
 * Action portée par un besoin.
 *
 * L'icône ne porte jamais l'action seule : le libellé reste écrit, et la cible
 * garde 36 px de haut — l'écran se travaille au doigt sur tablette, où un lien
 * textuel de 16 px se rate une fois sur deux.
 */
function ActionBesoin({
  libelle,
  icone,
  iconeLucide: IconeLucide,
  onClick,
  disabled = false,
  ton = 'neutre',
  compact = false,
  infobulle,
}: {
  libelle: string;
  /** Trait maison, dessiné dans le jeu `TRACES_ICONE`. */
  icone?: keyof typeof TRACES_ICONE;
  /** Icône Lucide, pour les actions ajoutées depuis que la page en dépend déjà. */
  iconeLucide?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  ton?: 'neutre' | 'danger';
  /** Icône seule, carrée : pour les gestes sur la fiche, alignés à droite. */
  compact?: boolean;
  /** Intitulé complet, quand le libellé du bouton est abrégé pour tenir la ligne. */
  infobulle?: string;
}): JSX.Element {
  const classeIcone = compact ? 'h-[18px] w-[18px] shrink-0' : 'h-4 w-4 shrink-0';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      // Même en icône seule, le libellé reste lisible par un lecteur d'écran et
      // par le survol : une corbeille sans nom n'est pas une action nommée.
      aria-label={compact ? libelle : infobulle}
      title={compact ? libelle : infobulle}
      className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg border font-semibold transition disabled:opacity-50 ${
        compact
          ? 'h-10 w-10 sm:h-11 sm:w-11'
          : 'min-h-[40px] gap-1.5 px-2.5 text-[11px] sm:min-h-[44px] sm:px-3 sm:text-xs'
      } ${
        ton === 'danger'
          ? 'border-red-200 bg-white text-red-600 hover:bg-red-50'
          : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
      }`}
    >
      {IconeLucide ? (
        <IconeLucide className={classeIcone} strokeWidth={1.8} aria-hidden="true" />
      ) : (
        <svg
          className={classeIcone}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          {TRACES_ICONE[icone!]}
        </svg>
      )}
      {!compact && libelle}
    </button>
  );
}

/**
 * Fenêtre de la fiche de recueil des besoins (CDC §5.1).
 *
 * Le formulaire est remonté par sa `key` à chaque entreprise : les champs d'un
 * besoin ne peuvent pas se retrouver sur l'entreprise suivante. La fenêtre ne
 * s'ouvre que sur une entreprise choisie — un formulaire pré-rempli sur une
 * entreprise que la personne n'a pas sélectionnée créerait le besoin au mauvais
 * endroit.
 */

/**
 * Ligne du répertoire commercial lue comme une fiche d'organisation.
 *
 * La fenêtre de fiche est commune au répertoire : elle attend la forme rendue
 * par le dossier. La conversion est ici plutôt qu'un second appel à l'API —
 * tout ce dont le formulaire a besoin est déjà sur la ligne.
 */
function ficheDeLaLigne(entreprise: LigneEntreprise): FicheOrganisation {
  return {
    id: entreprise.id,
    genre: 'ENTREPRISE',
    nom: entreprise.denomination,
    type: entreprise.statutRelation,
    typeLibelle: entreprise.statutLibelle,
    typeTon: entreprise.statutTon,
    sites: entreprise.sites,
    siret: entreprise.siret,
    siren: entreprise.siret.slice(0, 9),
    rna: null,
    activite: entreprise.secteur,
    adresse: entreprise.adresse,
    codePostal: entreprise.codePostal,
    ville: entreprise.ville,
    telephone: entreprise.telephone,
    email: entreprise.email,
    siteWeb: entreprise.siteWeb,
    contact: entreprise.contact,
    gerant: entreprise.gerant,
    contactOperationnel: entreprise.contactOperationnel,
    numeroVoie: entreprise.numeroVoie,
    voie: entreprise.voie,
    complementAdresse: entreprise.complementAdresse,
    secteurEmployeur: entreprise.secteurEmployeur,
    typeEmployeur: entreprise.typeEmployeur,
    employeurSpecifique: entreprise.employeurSpecifique,
    caisseRetraite: entreprise.caisseRetraite,
    codeNaf: entreprise.codeNaf,
    opco: entreprise.opco,
    idcc: entreprise.idcc,
    effectif: entreprise.effectif,
    dernierContactLe: entreprise.dernierContactLe,
    relanceAttendue: entreprise.relanceAttendue,
    actif: true,
    creeLe: '',
    modifieLe: '',
  };
}
